import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { toast } from 'sonner';
import {
  Target,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Calendar,
  DollarSign,
  TrendingUp,
  Users,
  Eye,
  MousePointer,
  CheckCircle,
  Clock,
  Pause,
  Play,
  Archive,
  BarChart3 } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200'
};

const statusIcons = {
  draft: Clock,
  active: Play,
  paused: Pause,
  completed: CheckCircle,
  cancelled: Archive
};

// Mock campaigns for demo
const mockCampaigns = [
{
  id: 1,
  name: 'Black Friday 2025',
  description: 'Major promotional campaign for Black Friday sales',
  objective: 'conversions',
  status: 'active',
  start_date: '2025-01-20',
  end_date: '2025-01-27',
  total_budget: 5000,
  spent: 2340,
  impressions: 125000,
  clicks: 4500,
  conversions: 234,
  engagement: 12500
},
{
  id: 2,
  name: 'New Year Launch',
  description: 'Product launch campaign for new collection',
  objective: 'awareness',
  status: 'draft',
  start_date: '2025-02-01',
  end_date: '2025-02-15',
  total_budget: 3000,
  spent: 0,
  impressions: 0,
  clicks: 0,
  conversions: 0,
  engagement: 0
},
{
  id: 3,
  name: 'Summer Sale',
  description: 'Summer clearance and new arrivals',
  objective: 'traffic',
  status: 'paused',
  start_date: '2025-01-10',
  end_date: '2025-01-31',
  total_budget: 2000,
  spent: 890,
  impressions: 45000,
  clicks: 1800,
  conversions: 67,
  engagement: 3400
}];


export const Campaigns = () => {const { t } = useTranslation("cns");
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState(mockCampaigns);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    objective: 'awareness',
    total_budget: ''
  });

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const createCampaign = () => {
    if (!newCampaign.name) {
      toast.error('Please enter a campaign name');
      return;
    }

    const campaign = {
      id: Date.now(),
      ...newCampaign,
      status: 'draft',
      total_budget: parseFloat(newCampaign.total_budget) || 0,
      spent: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      engagement: 0
    };

    setCampaigns([campaign, ...campaigns]);
    toast.success('Campaign created!');
    setShowCreateModal(false);
    setNewCampaign({ name: '', description: '', objective: 'awareness', total_budget: '' });
  };

  const getObjectiveIcon = (objective) => {
    switch (objective) {
      case 'awareness':return Eye;
      case 'traffic':return MousePointer;
      case 'engagement':return Users;
      case 'conversions':return Target;
      case 'leads':return Users;
      default:return BarChart3;
    }
  };

  return (
    <div className="space-y-6" data-testid="campaigns">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl">
              <Target className="h-7 w-7 text-white" />
            </div>{t("campaigns", "Campaigns")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("createAndManage", "Create and manage your marketing campaigns")}

          </p>
        </div>
        <Button
          className="gap-2 bg-gradient-to-r from-orange-500 to-amber-600"
          onClick={() => setShowCreateModal(true)}>
          
          <Plus className="h-4 w-4" />{t("newCampaign", "New Campaign")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalCampaigns", "Total Campaigns")}</p>
                <p className="text-2xl font-bold">{campaigns.length}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <Target className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("active", "Active")}</p>
                <p className="text-2xl font-bold">{campaigns.filter((c) => c.status === 'active').length}</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Play className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalBudget", "Total Budget")}</p>
                <p className="text-2xl font-bold">{t("r", "R$")}{campaigns.reduce((sum, c) => sum + (c.total_budget || 0), 0).toLocaleString()}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalConversions", "Total Conversions")}</p>
                <p className="text-2xl font-bold">{campaigns.reduce((sum, c) => sum + (c.conversions || 0), 0).toLocaleString()}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10" />
          
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'draft', 'paused', 'completed'].map((status) =>
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}>
            
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          )}
        </div>
      </div>

      {/* Campaign List */}
      {filteredCampaigns.length === 0 ?
      <Card className="card-elevated border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
              <Target className="h-10 w-10 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t("noCampaignsYet", "No Campaigns Yet")}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("createYourFirst", "Create your first campaign to start organizing your marketing efforts.")}

          </p>
            <Button
            className="gap-2 bg-gradient-to-r from-orange-500 to-amber-600"
            onClick={() => setShowCreateModal(true)}>
            
              <Plus className="h-4 w-4" />{t("createCampaign", "Create Campaign")}

          </Button>
          </CardContent>
        </Card> :

      <div className="space-y-4">
          {filteredCampaigns.map((campaign) => {
          const StatusIcon = statusIcons[campaign.status] || Clock;
          const ObjectiveIcon = getObjectiveIcon(campaign.objective);
          const budgetProgress = campaign.total_budget ? campaign.spent / campaign.total_budget * 100 : 0;

          return (
            <Card key={campaign.id} className="card-elevated hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Campaign Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={statusColors[campaign.status]}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {campaign.status}
                        </Badge>
                        <Badge variant="outline" className="gap-1">
                          <ObjectiveIcon className="h-3 w-3" />
                          {campaign.objective}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold mb-1">{campaign.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">{campaign.description}</p>
                      {campaign.start_date &&
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(campaign.start_date).toLocaleDateString()} - {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : 'Ongoing'}
                        </p>
                    }
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold">{(campaign.impressions / 1000).toFixed(1)}K</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <Eye className="h-3 w-3" />{t("impressions", "Impressions")}
                      </p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{campaign.clicks.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <MousePointer className="h-3 w-3" />{t("clicks", "Clicks")}
                      </p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{campaign.conversions}</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <Target className="h-3 w-3" />{t("conversions", "Conversions")}
                      </p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold">{campaign.engagement.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <Users className="h-3 w-3" />{t("engagement", "Engagement")}
                      </p>
                      </div>
                    </div>

                    {/* Budget */}
                    <div className="lg:w-48">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-muted-foreground">{t("budget", "Budget")}</span>
                        <span className="font-medium">{t("r", "R$")}{campaign.spent?.toLocaleString()} / {campaign.total_budget?.toLocaleString()}</span>
                      </div>
                      <Progress value={budgetProgress} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1 text-right">
                        {budgetProgress.toFixed(0)}{t("used", "% used")}
                    </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 lg:flex-col">
                      <Button variant="outline" size="sm" className="flex-1 lg:flex-none">{t("edit", "Edit")}

                    </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>);

        })}
        </div>
      }

      {/* Create Modal */}
      {showCreateModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-orange-500" />{t("createNewCampaign", "Create New Campaign")}

            </CardTitle>
              <CardDescription>{t("setUpA", "Set up a new marketing campaign")}

            </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t("campaignName", "Campaign Name")}</label>
                <Input
                placeholder="Enter campaign name..."
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })} />
              
              </div>
              <div>
                <label className="text-sm font-medium">{t("description", "Description")}</label>
                <Textarea
                placeholder="Describe your campaign..."
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                rows={3} />
              
              </div>
              <div>
                <label className="text-sm font-medium">{t("objective", "Objective")}</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['awareness', 'traffic', 'engagement', 'conversions', 'leads'].map((obj) =>
                <Button
                  key={obj}
                  variant={newCampaign.objective === obj ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewCampaign({ ...newCampaign, objective: obj })}>
                  
                      {obj.charAt(0).toUpperCase() + obj.slice(1)}
                    </Button>
                )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t("budgetR", "Budget (R$)")}</label>
                <Input
                type="number"
                placeholder="0.00"
                value={newCampaign.total_budget}
                onChange={(e) => setNewCampaign({ ...newCampaign, total_budget: e.target.value })} />
              
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>{t("cancel", "Cancel")}

              </Button>
                <Button
                className="bg-gradient-to-r from-orange-500 to-amber-600"
                onClick={createCampaign}>{t("createCampaign", "Create Campaign")}


              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </div>);

};

export default Campaigns;